import { useState } from 'react';
import { useTranslation } from '@doccareplus/shared/lib';
import { detectDefaultCountry, dialCodeFor, isValidNationalPhoneNumber, toE164 } from '@doccareplus/shared/lib/phone';
import { selectActiveContext, useAppSelector } from '@doccareplus/kernel';
import { checkPatientDuplicates, useRegisterPatientCommand } from '../../application/patients.command';
import type { CreatePatientRequest, PatientDuplicateMatchResponse } from '../../infrastructure/patients.api';

export interface RegisterPatientFields {
  fullName: string;
  /** Phone country as an ISO 3166-1 alpha-2 code (e.g. "IN"); supplies the dialing code. */
  phoneCountry: string;
  /** National phone number (digits); composed with the country dial code into E.164 on submit. */
  phone: string;
  dateOfBirth: string;
  gender: string;
  bloodGroup: string;
  email: string;
  allergySummary: string;
  address: string;
  notes: string;
}

/**
 * Fresh empty form. The phone country defaults to the active clinic's country when known (so most
 * registrations start on the right dialing code), falling back to the user's detected country. Still
 * changeable via the picker.
 */
function emptyFields(defaultCountry?: string): RegisterPatientFields {
  return {
    fullName: '',
    phoneCountry: defaultCountry || detectDefaultCountry(),
    phone: '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: '',
    email: '',
    allergySummary: '',
    address: '',
    notes: '',
  };
}

export interface UseRegisterPatientForm {
  fields: RegisterPatientFields;
  setField: (name: keyof RegisterPatientFields, value: string) => void;
  isSubmitting: boolean;
  error: string | null;
  duplicateMatches: PatientDuplicateMatchResponse[];
  duplicateReason: string;
  setDuplicateReason: (value: string) => void;
  requiresDuplicateReview: boolean;
  /** Submits the registration. Resolves the new patient id on success, else null. */
  submit: () => Promise<string | null>;
}

/**
 * useRegisterPatientForm — feature UI state + orchestration for registering a
 * patient. Local field state; the write is delegated to the register command.
 * Name and a country-valid phone are required. Shared by the
 * desktop modal and mobile bottom-sheet surfaces.
 */
export function useRegisterPatientForm(): UseRegisterPatientForm {
  const { t } = useTranslation('patients');
  const registerCommand = useRegisterPatientCommand();
  const clinicCountry = useAppSelector(selectActiveContext)?.country;

  const [fields, setFields] = useState<RegisterPatientFields>(() => emptyFields(clinicCountry));
  const [error, setError] = useState<string | null>(null);
  const [duplicateMatches, setDuplicateMatches] = useState<PatientDuplicateMatchResponse[]>([]);
  const [duplicateReason, setDuplicateReason] = useState('');
  const [reviewedFingerprint, setReviewedFingerprint] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const setField = (name: keyof RegisterPatientFields, value: string) => {
    setFields((current) => ({ ...current, [name]: value }));
    if (name === 'fullName' || name === 'phoneCountry' || name === 'phone' || name === 'email' || name === 'dateOfBirth') {
      setDuplicateMatches([]); setDuplicateReason(''); setReviewedFingerprint(null);
    }
  };

  const submit = async (): Promise<string | null> => {
    setError(null);
    const fullName = fields.fullName.trim();
    if (!fullName) {
      setError(t('register.errors.nameRequired'));
      return null;
    }

    // Phone is required (accepted 2026-07-15); compose the national number + country dial code into E.164.
    const phone = toE164(dialCodeFor(fields.phoneCountry) ?? '', fields.phone);
    if (!phone) {
      setError(t('register.errors.phoneRequired'));
      return null;
    }
    if (!isValidNationalPhoneNumber(fields.phoneCountry, fields.phone)) {
      setError(fields.phoneCountry === 'IN' ? t('register.errors.phoneIndiaLength') : t('register.errors.phoneInvalid'));
      return null;
    }

    const body: CreatePatientRequest = {
      fullName,
      phone,
      dateOfBirth: emptyToUndefined(fields.dateOfBirth),
      gender: emptyToUndefined(fields.gender),
      bloodGroup: emptyToUndefined(fields.bloodGroup),
      email: emptyToUndefined(fields.email),
      allergySummary: emptyToUndefined(fields.allergySummary),
      address: emptyToUndefined(fields.address),
      notes: emptyToUndefined(fields.notes),
    };

    const fingerprint = [fullName.toLowerCase(), phone, body.email ?? '', body.dateOfBirth ?? ''].join('|');
    if (reviewedFingerprint !== fingerprint) {
      try {
        setIsChecking(true);
        const matches = await checkPatientDuplicates({ fullName, phone, email: body.email, dateOfBirth: body.dateOfBirth });
        setReviewedFingerprint(fingerprint);
        setDuplicateMatches(matches);
        if (matches.length > 0) return null;
      } catch (err) {
        setError((err as Error).message || t('register.errors.duplicateCheckFailed'));
        return null;
      } finally { setIsChecking(false); }
    }

    if (duplicateMatches.length > 0) {
      if (duplicateReason.trim().length < 5) { setError(t('register.errors.duplicateReasonRequired')); return null; }
      body.duplicateOverrideReason = duplicateReason.trim();
    }

    try {
      const created = await registerCommand.mutateAsync(body);
      setFields(emptyFields(clinicCountry));
      setDuplicateMatches([]); setDuplicateReason(''); setReviewedFingerprint(null);
      return created.id;
    } catch (err) {
      setError((err as Error).message || t('register.errors.failed'));
      return null;
    }
  };

  return {
    fields,
    setField,
    isSubmitting: registerCommand.isPending || isChecking,
    error,
    duplicateMatches,
    duplicateReason,
    setDuplicateReason,
    requiresDuplicateReview: duplicateMatches.length > 0,
    submit,
  };
}

function emptyToUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}
